import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import registerUserService from "../services/registerServices"; // Import the registration service
import { verifyOtpService } from "../services/loginServices"; // Import OTP verification service

// Define the initial state
const initialState = {
  user: null,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  otpStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  otpError: null, // Error for OTP verification
  emailForOtp: null, // Email used for OTP
};

// Define async thunk for registration request using the external service
export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async ({ userData, lang }, { rejectWithValue }) => {
    try {
      const response = await registerUserService({ userData, lang }); // Use the registration service
      return { ...response.data.data, email: userData.email }; // Return the response for further handling in the reducer
    } catch (error) {
      return rejectWithValue(error.response?.data || "Registration failed"); // Handle error and pass it to the reducer
    }
  }
);

// Define async thunk for OTP verification
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await verifyOtpService(email, otp); // Call OTP verification service
      return response.data.data; // Return data upon successful OTP verification
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to verify OTP");
    }
  }
);

// Create the slice
const registerSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearOtpError: (state) => {
      state.otpError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle registration cases
      .addCase(registerUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.status = "success";
        state.user = action.payload; // Store the user data (e.g., token)
        state.emailForOtp = action.payload.email; // Store email for OTP
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.data[0]?.msg || "Registration failed";
      })

      // Handle OTP verification cases
      .addCase(verifyOtp.pending, (state) => {
        state.otpStatus = "loading";
        state.otpError = null;
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.otpStatus = "success";
        state.otpError = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpStatus = "failed";
        state.otpError = action.payload?.message || "Failed to verify OTP";
      });
  },
});

// Export actions and reducer
export const { clearError, clearOtpError } = registerSlice.actions;
export default registerSlice.reducer;
